import React, { useEffect, useState } from "react";
import PropRow, { PropInputType } from "./PropRow";

import { toast } from "sonner";
import { DELETE_PROP_CONSTANT } from "../../utils/utils";
import FileEditor from "@/components/CodeEditors/FileEditor";
import Dropdown from "rc-dropdown";
import Menu, { Item as MenuItem } from "rc-menu";
import Icon, { ICONS } from "../Core/Icons";
import {
  ChangePropChange,
  ChangeTagChange,
} from "@/store/zustand/changeLedgerTypes";
import { useChangeLedgerStore } from "@/store/zustand/changeLedger";
import { useSelectedNavTreeNode } from "@/store/zustand/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";

interface Props {
  selectedTreeElement?: any;
  inspectElementResponse: any;
  loadingProps: boolean;
}

const PROPS_TO_SKIP = {
  className: true,
  style: true,
  tempoelementid: true,
};

const PropManager: React.FC<Props> = (props) => {
  const addChange = useChangeLedgerStore((state) => state.addChange);

  const [extraPropsOpen, setExtraPropsOpen] = useState(false);
  const [unsetPropValues, setUnsetPropValues] = useState<any>({});
  const [addedProps, setAddedProps] = useState<any>({});
  const [deletedProps, setDeletedProps] = useState<any>({});

  const selectedNavTreeNode = useSelectedNavTreeNode();

  const tagName = selectedNavTreeNode?.name || "";

  useEffect(() => {
    // Clear the immediate change cache when the element is re-inspected
    setUnsetPropValues({});
    setAddedProps({});
    setDeletedProps({});
  }, [props.inspectElementResponse]);

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const revealLocation = useCodebaseStore((state) => state.revealLocation);

  const componentTreeElement =
    treeElementLookup?.[props.selectedTreeElement?.componentCodebaseId];

  const inspectedProps = {
    ...props.inspectElementResponse?.props,
    ...addedProps,
  };

  Object.keys(deletedProps).forEach((propKey) => {
    delete inspectedProps[propKey];
  });

  const unusedProps =
    componentTreeElement?.props && selectedNavTreeNode?.isComponent
      ? Object.keys(componentTreeElement.props).filter(
          (propKey) => !inspectedProps?.[propKey]
        )
      : [];

  const getRowForProp = (propKey: string, unused: boolean) => {
    if (PROPS_TO_SKIP[propKey]) {
      return null;
    }

    let value = unused ? unsetPropValues[propKey] : inspectedProps[propKey];
    let type = unused
      ? componentTreeElement.props[propKey]?.type?.name
      : typeof value;

    let inputType = null;
    if (value === "TEMPO_FUNCTION_TYPE" || type === "func") {
      // @ts-expect-error TS2322
      inputType = PropInputType.Code;
    } else if (type === "string") {
      // @ts-expect-error TS2322
      inputType = PropInputType.String;
    } else if (type === "number") {
      // @ts-expect-error TS2322
      inputType = PropInputType.Number;
    } else if (type === "boolean" || type === "bool") {
      // @ts-expect-error TS2322
      inputType = PropInputType.Bool;
      value = value ? "True" : "False";
    } else {
      console.error(
        "UNKNOWN PROP TYPE: ",
        propKey,
        value,
        typeof value,
        unused
      );
      return null;
    }

    return (
      <PropRow
        key={propKey}
        title={propKey}
        // @ts-expect-error TS2322
        inputType={inputType}
        value={value}
        unused={unused}
        onRemove={() => {
          if (unused) {
            return;
          }

          addChange(
            new ChangePropChange({
              // @ts-expect-error TS2322
              codebaseIdToChange: selectedNavTreeNode?.tempoElement.codebaseId,
              propName: propKey,
              // @ts-expect-error TS2322
              propValue: DELETE_PROP_CONSTANT,
            })
          );

          setAddedProps({
            ...addedProps,
            [propKey]: DELETE_PROP_CONSTANT,
          });

          setDeletedProps({
            ...deletedProps,
            [propKey]: true,
          });
        }}
        onAdd={() => {
          if (!unused || !value) {
            return;
          }

          addChange(
            new ChangePropChange({
              // @ts-expect-error TS2322
              codebaseIdToChange: selectedNavTreeNode?.tempoElement.codebaseId,
              propName: propKey,
              propValue: unsetPropValues[propKey],
            })
          );

          setAddedProps({
            ...addedProps,
            [propKey]: unsetPropValues[propKey],
          });

          // Remove prop if deleted
          const newDeletedProps = { ...deletedProps };
          delete newDeletedProps[propKey];
          setDeletedProps(newDeletedProps);
        }}
        onValueChange={(newValue: any) => {
          if (unused) {
            setUnsetPropValues({
              ...unsetPropValues,
              [propKey]: newValue,
            });
            return;
          }

          addChange(
            new ChangePropChange({
              // @ts-expect-error TS2322
              codebaseIdToChange: selectedNavTreeNode?.tempoElement.codebaseId,
              propName: propKey,
              propValue: newValue,
            })
          );
        }}
        onViewCode={() => {
          if (unused) {
            return;
          }

          const propInCodebase = (props.selectedTreeElement?.propLocations ||
            {})[propKey];
          if (!propInCodebase) {
            toast.error("Could not find prop in codebase");
            return;
          }

          revealLocation({
            revealLocationAt: propInCodebase.shouldAttemptJump
              ? propInCodebase.end - 1
              : propInCodebase.start,
            attemptJumpToDefinition: propInCodebase.shouldAttemptJump,
          });
        }}
      />
    );
  };

  const TYPOGRAPHY_TYPES = ["P", "H1", "H2", "H3", "H4", "H5", "H6", "DIV"];

  const renderChangeTypographyTag = () => {
    const changeTagType = (newTag: string) => {
      if (!TYPOGRAPHY_TYPES.includes(newTag) || tagName === newTag) {
        return;
      }

      addChange(
        new ChangeTagChange({
          // @ts-expect-error TS2322
          codebaseIdToChange: selectedNavTreeNode?.tempoElement.codebaseId,
          newTagName: newTag.toLowerCase(),
        })
      );
    };

    return (
      <div className="change-tag-section flex flex-col p-2 gap-y-2">
        <p className="text-xs font-normal">Change tag type</p>
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu
              onSelect={(info: any) => {
                changeTagType(info.key);
              }}
              className="drop-menu"
              style={{
                overflow: "auto",
              }}
            >
              {TYPOGRAPHY_TYPES.map((tag: string) => {
                return <MenuItem key={tag}>{tag.toLowerCase()}</MenuItem>;
              })}
            </Menu>
          }
        >
          <div className={"tag-dropdown"}>
            <div className={"tag-text"}>{tagName.toLowerCase()}</div>
            <Icon
              icon={ICONS.CHEVRON_DOWN}
              style={{
                width: 20,
                height: 20,
              }}
            />
          </div>
        </Dropdown>
      </div>
    );
  };

  return (
    <div className="section prop-manager">
      <div className="section-header">
        <div className="section-header-label">PROPS</div>
      </div>
      {TYPOGRAPHY_TYPES.includes(tagName) && renderChangeTypographyTag()}
      {props.loadingProps && (
        <img
          src={"/images/loader-anim.svg"}
          style={{ width: 28, height: 28 }}
          alt={"Loader Spinner"}
          width={28}
          height={28}
          className="loading-props-image max-w-none"
        />
      )}
      {props.inspectElementResponse?.response?.error && (
        <div className="error-row">
          {props.inspectElementResponse.response.error}
        </div>
      )}
      {inspectedProps
        ? Object.keys(inspectedProps).map((propKey: any) =>
            getRowForProp(propKey, false)
          )
        : null}
      {unusedProps.length > 0 ? (
        <div className="unused-props-header">
          <img
            src={"/images/chevron.svg"}
            className={
              extraPropsOpen
                ? "chevron max-w-none open"
                : "chevron max-w-none closed"
            }
            width={10}
            height={10}
            onClick={() => {
              setExtraPropsOpen(!extraPropsOpen);
            }}
            alt={"Chevron"}
          />
          <div
            style={{ cursor: "pointer" }}
            onClick={() => setExtraPropsOpen(!extraPropsOpen)}
          >
            See {unusedProps.length} unused Props
          </div>
        </div>
      ) : null}
      {extraPropsOpen
        ? unusedProps.map((propKey: any) => getRowForProp(propKey, true))
        : null}

      {props.selectedTreeElement?.filename &&
        props.selectedTreeElement?.staticTextContents && (
          <div>
            <div className="section-header">
              <div className="section-header-label">CONTENTS</div>
            </div>
            <div className="content-editor-wrapper">
              <FileEditor
                filename={props.selectedTreeElement?.filename}
                substringStart={props.selectedTreeElement?.contentsStart}
                substringEnd={props.selectedTreeElement?.contentsEnd}
                trimWhitespace={true}
                forceLanguage="text"
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default React.memo(PropManager);
